import React from 'react'
import { Observer, observer } from 'mobx-react'
import fieldTranslator from '../../../../../helpers/fieldTranslator'
import formFieldManager from '../formFieldsManager/formFieldManager'
import clsx from 'clsx'
import { isObservableArray } from 'mobx'
import { sortListByPosition } from '../../../../../helpers/generalHelpers'

/*
  This Component receives a form which itself is considered a field for the form maker.
  it has a type which determines the field shown in the form maker
  and other options which will be used as options
*/

const SingleField = ({ fieldForm, isPreview }) => {
  const Field = observer(fieldTranslator(fieldForm.$('field_type').$value))
  const fieldId = fieldForm.$('id').$value

  return (
    <Observer>
      {() => {
        const getClassNames = (fieldId) => {
          const classNames = clsx({
            'single-form-field': true,
            'has-focus': formFieldManager.focusedFieldId === fieldId,
          })
          return classNames
        }

        const focusDispatcher = (fieldId) => (event) => {
          event.stopPropagation()
          formFieldManager.fieldHasGainedFocus(fieldId)
        }

        return (
          <div
            className={getClassNames(fieldId)}
            onClick={focusDispatcher(fieldId)}
          >
            <Field
              {...generateFieldProps(fieldForm)}
              isPreview={isPreview}
              customized
            />
          </div>
        )
      }}
    </Observer>
  )
}

export default SingleField

const flatten = (acc, value) => ({ ...acc, ...value })

const generateFieldProps = (fieldForm) => ({
  ...generateCommonFieldProps(fieldForm),
  ...generateSpecificFieldProps(fieldForm),
})

const readValueOf = (fieldForm) => (prop) => fieldForm.$(prop).$value

const generateCommonFieldProps = (fieldForm) => {
  const readValueOfField = readValueOf(fieldForm)
  const errors = Object.values(fieldForm.errors())
    .filter(Boolean)
    .map((error) => {
      if (error && typeof error === 'object') {
        return Object.values(error)
          .filter(Boolean)
          .map((errorMessage) => `- ${errorMessage}`)
          .join('')
      }
      return error
    })
    .filter(Boolean)

  const validatorError = fieldForm.validator && fieldForm.validator.error
  const totalErrors = errors.length > 0 ? errors : validatorError
  return {
    label: readValueOfField('title'),
    description: readValueOfField('description'),
    required: readValueOfField('required'),
    admin_only: readValueOfField('admin_only'),
    is_random_sortable: readValueOfField('is_random_sortable'),
    value: readValueOfField('current_value'),
    error: totalErrors || undefined, // if error was null filter it
    touched: true,
    onChange: fieldForm.$('current_value').onChange,
    slug: readValueOfField('slug'),
  }
}

export const specificFieldDictionary = {
  number: [
    {
      propName: 'min_value',
      fieldNameInFormField: 'min_value_selector',
      alwaysSend: true,
    },
    {
      propName: 'max_value',
      fieldNameInFormField: 'max_value_selector',
      alwaysSend: true,
    },
    {
      propName: 'decimal_places',
      fieldNameInFormField: 'decimal_places',
      alwaysSend: true,
    },
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  money: [
    { propName: 'min_value', fieldNameInFormField: 'min_value_selector' },
    { propName: 'max_value', fieldNameInFormField: 'max_value_selector' },
    { propName: 'currency', fieldNameInFormField: 'currency_selector' },
    { propName: 'default', fieldNameInFormField: 'default' },
  ],

  short_text: [
    { propName: 'max_length', fieldNameInFormField: 'max_length_selector' },
    { propName: 'json_key', fieldNameInFormField: 'json_key' },
    { propName: 'unique', fieldNameInFormField: 'unique' },
    {
      propName: 'acceptable_answers',
      fieldNameInFormField: 'acceptable_answers',
      alwaysSend: true,
      updatable: true,
      updateFrom: 'acceptable_answers',
    },
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  long_text: [
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  email: [
    { propName: 'unique', fieldNameInFormField: 'unique' },
    {
      propName: 'acceptable_answers',
      fieldNameInFormField: 'acceptable_answers',
      alwaysSend: true,
      updatable: true,
      updateFrom: 'acceptable_answers',
    },
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  website: [
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  phone: [
    { propName: 'phone_type', fieldNameInFormField: 'phone_type_selector' },
    { propName: 'unique', fieldNameInFormField: 'unique' },
    {
      propName: 'acceptable_answers',
      fieldNameInFormField: 'acceptable_answers',
      alwaysSend: true,
      updatable: true,
      updateFrom: 'acceptable_answers',
    },
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  phone_verification: [
    { propName: 'unique', fieldNameInFormField: 'unique' },
    {
      propName: 'acceptable_answers',
      fieldNameInFormField: 'acceptable_answers',
      alwaysSend: true,
      updatable: true,
      updateFrom: 'acceptable_answers',
    },
  ],

  email_verification: [
    { propName: 'unique', fieldNameInFormField: 'unique' },
    {
      propName: 'acceptable_answers',
      fieldNameInFormField: 'acceptable_answers',
      alwaysSend: true,
      updatable: true,
      updateFrom: 'acceptable_answers',
    },
  ],

  choice: [
    {
      propName: 'options',
      fieldNameInFormField: 'choice_items_creator',
    },
    {
      propName: 'bulk_choices',
      fieldNameInFormField: 'bulk_choices',
      clearOnUpdate: true,
    },
    { propName: 'has_other_choice', fieldNameInFormField: 'has_other_choice' },
    { propName: 'shuffle_choices', fieldNameInFormField: 'shuffle_choices' },
    { propName: 'default', fieldNameInFormField: 'default', alwaysSend: true },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  dropdown: [
    {
      propName: 'options',
      fieldNameInFormField: 'choice_items_creator',
    },
    {
      propName: 'bulk_choices',
      fieldNameInFormField: 'bulk_choices',
      clearOnUpdate: true,
    },
    { propName: 'default', fieldNameInFormField: 'default', alwaysSend: true },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  multiple_select: [
    {
      propName: 'options',
      fieldNameInFormField: 'choice_items_creator',
    },
    {
      propName: 'bulk_choices',
      fieldNameInFormField: 'bulk_choices',
      clearOnUpdate: true,
    },
    { propName: 'has_other_choice', fieldNameInFormField: 'has_other_choice' },
    { propName: 'shuffle_choices', fieldNameInFormField: 'shuffle_choices' },
    {
      propName: 'min_selectable_choices',
      fieldNameInFormField: 'min_selectable_choices',
      alwaysSend: true,
    },
    {
      propName: 'max_selectable_choices',
      fieldNameInFormField: 'max_selectable_choices',
      alwaysSend: true,
    },
    { propName: 'default', fieldNameInFormField: 'default', alwaysSend: true },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  matrix: [
    { propName: 'options', fieldNameInFormField: 'choice_items_creator' },
    {
      propName: 'choice_groups',
      fieldNameInFormField: 'choice_groups_creator',
    },
  ],

  time: [
    { propName: 'from_time', fieldNameInFormField: 'from_time_selector' },
    { propName: 'to_time', fieldNameInFormField: 'to_time_selector' },
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  date: [
    { propName: 'date_format', fieldNameInFormField: 'date_format' },
    {
      propName: 'from_date',
      fieldNameInFormField: 'from_date_selector',
      alwaysSend: true,
    },
    {
      propName: 'to_date',
      fieldNameInFormField: 'to_date_selector',
      alwaysSend: true,
    },
    {
      propName: 'relative_range_start',
      fieldNameInFormField: 'relative_range_start',
      alwaysSend: true,
    },
    {
      propName: 'relative_range_end',
      fieldNameInFormField: 'relative_range_end',
      alwaysSend: true,
    },
    {
      propName: 'default_date',
      fieldNameInFormField: 'default_date',
      alwaysSend: true,
    },
    {
      propName: 'custom_default_date',
      fieldNameInFormField: 'custom_default_date',
      alwaysSend: true,
    },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  file: [
    { propName: 'file_type', fieldNameInFormField: 'file_type_selector' },
    { propName: 'max_size', fieldNameInFormField: 'max_size_selector' },
    {
      propName: 'allow_multiple_files',
      fieldNameInFormField: 'allow_multiple_files',
    },
    { propName: 'min_files_count', fieldNameInFormField: 'min_files_count' },
    { propName: 'max_files_count', fieldNameInFormField: 'max_files_count' },
    {
      propName: 'allowed_extensions',
      fieldNameInFormField: 'allowed_extensions',
    },
  ],

  // NOTE:         'type' here is just used to distinguish the yes_no field from choice field
  // it's value is not dynamic and is set in the declarationGenerator statically
  yes_no: [
    { propName: 'type', fieldNameInFormField: 'type' },
    { propName: 'default', fieldNameInFormField: 'default' },
  ],

  rating: [
    { propName: 'sub_type', fieldNameInFormField: 'sub_type' },
    { propName: 'min_message', fieldNameInFormField: 'min_message_selector' },
    { propName: 'max_message', fieldNameInFormField: 'max_message_selector' },
    { propName: 'thumbnail_type', fieldNameInFormField: 'thumbnail_type' },
    {
      propName: 'range_start',
      fieldNameInFormField: 'range_start',
      alwaysSend: true,
    },
    {
      propName: 'range_end',
      fieldNameInFormField: 'range_end',
      alwaysSend: true,
    },
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],

  meta: [
    { propName: 'sub_type', fieldNameInFormField: 'sub_type' },
    { propName: 'video_link', fieldNameInFormField: 'video_link' },
  ],

  product: [
    { propName: 'min_value', fieldNameInFormField: 'min_value_selector' },
    { propName: 'max_value', fieldNameInFormField: 'max_value_selector' },
    { propName: 'unit_price', fieldNameInFormField: 'unit_price_selector' },
    { propName: 'images', fieldNameInFormField: 'images' },
    { propName: 'default', fieldNameInFormField: 'default' },
    {
      propName: 'is_subscription',
      fieldNameInFormField: 'is_subscription',
    },
    {
      propName: 'subscribed_item_id',
      fieldNameInFormField: 'subscribed_item_id',
    },
  ],
  hidden: [
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],
  table: [
    {
      propName: 'column_groups',
      fieldNameInFormField: 'column_groups_creator',
    },
    {
      propName: 'choice_groups',
      fieldNameInFormField: 'choice_groups_creator',
    },
  ],

  lookup: [
    { propName: 'related_form', fieldNameInFormField: 'related_form' },
    { propName: 'field', fieldNameInFormField: 'field' },
    { propName: 'add_new_choice', fieldNameInFormField: 'add_new_choice' },
    {
      propName: 'choice_items',
      fieldNameInFormField: 'choice_items',
      readOnly: true,
    },
  ],
  oembed: [
    { propName: 'url', fieldNameInFormField: 'url' },
    { propName: 'config', fieldNameInFormField: 'config' },
    {
      propName: 'oembed',
      fieldNameInFormField: 'oembed',
      readOnly: true,
    },
  ],
  variable: [
    { propName: 'sub_type', fieldNameInFormField: 'sub_type' },
    {
      propName: 'decimal_places',
      fieldNameInFormField: 'decimal_places',
      alwaysSend: true,
    },
    { propName: 'default', fieldNameInFormField: 'default' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],
  user: [
    { propName: 'user_form', fieldNameInFormField: 'user_form' },
    {
      propName: 'choice_items',
      fieldNameInFormField: 'choice_items',
      readOnly: true,
    },
    { propName: 'auto_assign', fieldNameInFormField: 'auto_assign' },
    { propName: 'alias', fieldNameInFormField: 'alias' },
  ],
}

const specificFieldDictionaryProxy = new Proxy(specificFieldDictionary, {
  get: function (target, property) {
    return target[property] ? target[property] : []
  },
})

const makeUpChoiceItems = (choiceItems) => {
  /*
    choice items need a [{slug, title}] input but the option creator field only
    give out [{title}], so we assign the slug the same value as the title
    just to make the choice fields work
  */

  if (!choiceItems || !isObservableArray(choiceItems)) {
    throw new TypeError(
      `makeUpChoiceItems expected an array received: ${typeof choiceItems} ${stringify(
        choiceItems
      )}`
    )
  }
  // sort options by their position
  const sortedChoices = sortListByPosition(choiceItems)

  return sortedChoices.map((choiceItem) => {
    if (!choiceItem.slug) {
      // NOTE: choice items here should not be modified, it is the state!
      return {
        slug: choiceItem.title,
        title: choiceItem.title,
        image: choiceItem.image,
      }
    }
    return choiceItem
  })
}

const stringify = (value) => JSON.stringify(value, null, 2)

const generateSpecificFieldProps = ((fieldsDictionary) => (fieldForm) => {
  const readValueOfField = readValueOf(fieldForm)
  const fieldType = readValueOfField('field_type')

  const fieldWithChoiceItems = [
    'choice',
    'dropdown',
    'multiple_select',
    'matrix',
  ]
  const allowedPropName = ['choice_groups', 'options']

  /*
    each option has a two props:
    1) "propName" which will be used as prop name for the field
    that will be showed as a preview to the user
    2) "fieldNameInFormField" which will be used to read propName value
    from the form manager
  */
  const fieldNameToFieldValue = ({ propName, fieldNameInFormField }) => {
    const needsMakeup =
      fieldWithChoiceItems.includes(fieldType) &&
      allowedPropName.includes(propName)

    const fieldSettingsValue = readValueOfField(fieldNameInFormField)

    return {
      [propName]: needsMakeup
        ? makeUpChoiceItems(fieldSettingsValue)
        : fieldSettingsValue,
    }
  }

  return fieldsDictionary[fieldType]
    .map(fieldNameToFieldValue)
    .reduce(flatten, {})
})(specificFieldDictionaryProxy)

// function isElementOutside(event) {
//   if (document.getElementById('focused-field-settings-box')) return !document
//     .getElementById('focused-field-settings-box')
//     .contains(event.target)
// }
